import axios from 'axios';
import moment from 'moment';
import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';

import BaseTabs from 'components/Base/BaseTabs';
import Completed from 'components/Base/Completed';
import BaseModal from 'components/Base/BaseModal';
import Loader from 'components/Base/Loader';

import IconEdit from './img/edit.png';
import IconReview from './img/review.png';
import IconArchive from './img/archive.png';
import IconDelete from './img/delete.png';
import IconCompleted from './img/done.svg';

const AthenaJobs = () => {
	const navigate = useNavigate();
	const [tabs, setTabs] = useState([
		{ value: 'pending', name: 'Pending', count: 0 },
		{ value: 'working', name: 'Accepted', count: 0 },
		{ value: 'completed', name: 'Completed', count: 0 },
		{ value: 'cancelled', name: 'Cancelled', count: 0 },
	]);

	const [appointments, setAppointments] = useState([]);
	const [currentTab, setCurrentTab] = useState(tabs[0].value);
	const [completed, setCompleted] = useState(false);
	const [fetching, setFetching] = useState(false);
	const [cancelReason, setCancelReason] = useState('');

	const [modalAppointment, setModalAppointment] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [showSlotsModal, setShowSlotsModal] = useState(false);
	const [availableSlots, setAvailableSlots] = useState([]);
	const [loadingSlots, setLoadingSlots] = useState(false);
	const [selectedSlot, setSelectedSlot] = useState(null);
	const [currentStep, setCurrentStep] = useState('slots');
	const [rescheduleReason, setRescheduleReason] = useState('');

	const tabAppointments = useMemo(() => {
		return appointments.filter((appointment) => appointment.status === currentTab) || [];
	}, [appointments, currentTab]);

	const fetchAppointments = useCallback(() => {
		setFetching(true);
		axios
			.get('/athena/appointments')
			.then(({ data }) => {
				setAppointments(data.appointments || []);

				setTabs((prev) => {
					return prev.map((item) => {
						item.count = data.appointments.filter((appointment) => appointment.status === item.value).length;
						return { ...item };
					});
				});
			})
			.catch((e) => {
				console.log(e);
			})
			.finally(() => {
				setFetching(false);
			});
	}, []);

	useEffect(() => {
		fetchAppointments();
	}, [fetchAppointments]);

	const sendCancelAppointment = useCallback(() => {
		if (!cancelReason.trim()) {
			return;
		}
		setFetching(true);
		axios.post(`/athena/appointment/${modalAppointment.jobId}/cancel`, {
			reason: cancelReason.trim()
		}, { timeout: 120000 })
			.then(() => {
				setCompleted(true);
				setShowModal(false);
				setCancelReason('');
				fetchAppointments();
			})
			.catch((error) => {
				console.error("Error cancelling appointment:", error);
			})
			.finally(() => {
				setFetching(false);
			});
	}, [fetchAppointments, modalAppointment, cancelReason]);

	const fetchAvailableSlots = useCallback(async (appointmentId, reasonId) => {
		setLoadingSlots(true);
		try {
			const appointmentDetailsResponse = await axios.get(`/athena/appointment/${appointmentId}/details`);
			const appointmentDetails = appointmentDetailsResponse.data[0];

			if (!appointmentDetails || !appointmentDetails.departmentid) {
				throw new Error('Could not get department information from appointment');
			}

			const response = await axios.post('/athena/appointment/open', {
				startDate: moment().format('YYYY-MM-DD'),
				endDate: moment().add(7, 'days').format('YYYY-MM-DD'),
				departmentId: appointmentDetails.departmentid,
				reasonId: reasonId
			}, { timeout: 120000 });
			setAvailableSlots(response.data);
		} catch (error) {
			console.error("Error fetching available slots:", error);
			setAvailableSlots([]);
		} finally {
			setLoadingSlots(false);
		}
	}, []);

	const handleReschedule = useCallback((appointment) => {
		setShowSlotsModal(true);
		fetchAvailableSlots(appointment.athenaAppointmentId, appointment.reasonId);
	}, [fetchAvailableSlots, modalAppointment]);

	const handleSlotSelection = useCallback((slot) => {
		setSelectedSlot(slot);
	}, []);

	const handleConfirmSlot = useCallback(() => {
		setCurrentStep('reason');
	}, []);

	const handleBackToSlots = useCallback(() => {
		setCurrentStep('slots');
		setRescheduleReason('');
	}, []);

	const handleConfirmReschedule = useCallback(async () => {
		if (!rescheduleReason.trim()) {
			return;
		}

		setFetching(true);

		try {
			await axios.post(`/athena/appointment/${modalAppointment.jobId}/reschedule`, {
				appointmentType: selectedSlot.appointmentType,
				reason: rescheduleReason.trim(),
				newappointmentid: selectedSlot.appointmentid,
				date: selectedSlot.date,
				startTime: selectedSlot.starttime,
			}, { timeout: 120000 })


			setCompleted(true);
			setShowSlotsModal(false);
			setCurrentStep('slots');
			setSelectedSlot(null);
			setRescheduleReason('');
			fetchAppointments();
		} catch (error) {
			console.error("Error rescheduling appointment:", error);
		} finally {
			setFetching(false);
		}
	}, [modalAppointment, selectedSlot, rescheduleReason, fetchAppointments]);

	const handleBookAgain = useCallback((appointment) => {
		navigate('/jobs/create/athena', {
			state: {
				previousAppointment: {
					providerId: appointment.providerId,
					departmentId: appointment.departmentId,
				}
			}
		});
	}, [navigate]);

	const getStatusColor = (status) => {
		switch (status) {
			case 'pending':
				return 'text-yellow-600 bg-yellow-100';
			case 'accepted':
				return 'text-blue-600 bg-blue-100';
			case 'completed':
				return 'text-green-600 bg-green-100';
			case 'cancelled':
				return 'text-red-600 bg-red-100';
			default:
				return 'text-gray-600 bg-gray-100';
		}
	};

	const getStatusText = (status) => {
		switch (status) {
			case 'pending':
				return 'Pending';
			case 'accepted':
				return 'Accepted';
			case 'completed':
				return 'Completed';
			case 'cancelled':
				return 'Cancelled';
			default:
				return status;
		}
	};

	return (
		<div className="page p-5 max-w-6xl mx-auto">
			<h1 className="text-4xl font-bold mb-8 text-gray-800">My Athena Appointments</h1>

			<Loader show={fetching} />
			<Completed show={completed} setShow={setCompleted} duration="750" />

			<BaseModal
				show={showModal === 'cancel'}
				onClose={() => {
					setShowModal(false);
					setCancelReason('');
				}}
				className="w-full max-w-[500px] mx-4 sm:mx-auto"
			>
				<div className="p-4">
					<div className="mb-6">
						<h2 className="text-xl sm:text-2xl font-bold text-gray-800">Cancel Appointment</h2>
						<p className="text-sm text-gray-600 mt-2">Are you sure you want to cancel this appointment?</p>
					</div>

					<div className="bg-gray-50 rounded-lg p-3 mb-6">
						<p className="text-sm text-gray-600 mb-2">Appointment Details:</p>
						<div className="font-medium text-gray-900">
							{modalAppointment?.title}
						</div>
						<div className="text-gray-600">
							{modalAppointment?.date && moment(modalAppointment.date).format('dddd, MMMM D, YYYY')}
						</div>
					</div>

					<div className="mb-6">
						<label htmlFor="cancelReason" className="block text-sm font-medium text-gray-700 mb-2">
							Reason for Cancellation
						</label>
						<textarea
							id="cancelReason"
							value={cancelReason}
							onChange={(e) => setCancelReason(e.target.value)}
							placeholder="Please provide a reason for cancelling..."
							className="w-full min-h-[100px] p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
						/>
						{cancelReason.trim() === '' && (
							<p className="mt-2 text-xs text-red-600">Please provide a reason for cancellation.</p>
						)}
					</div>

					<div className="flex justify-end gap-3">
						<button
							onClick={() => {
								setShowModal(false);
								setCancelReason('');
							}}
							className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
						>
							Cancel
						</button>
						<button
							onClick={sendCancelAppointment}
							disabled={!cancelReason.trim()}
							className={classNames(
								"px-6 py-2.5 text-sm font-medium rounded-lg transition-colors",
								cancelReason.trim()
									? "bg-red-600 text-white hover:bg-red-700"
									: "bg-red-300 text-white cursor-not-allowed"
							)}
						>
							Confirm Cancellation
						</button>
					</div>
				</div>
			</BaseModal>

			{/* Reschedule Modal */}
			<BaseModal
				show={showSlotsModal}
				onClose={() => {
					setShowSlotsModal(false);
					setSelectedSlot(null);
					setRescheduleReason('');
					setCurrentStep('slots');
				}}
				className="w-full max-w-[500px] mx-4 sm:mx-auto"
			>
				{currentStep === 'slots' ? (
					<div className="">
						<div className="mb-6">
							<h2 className="text-xl sm:text-2xl font-bold text-gray-800">Reschedule Appointment</h2>
							<p className="text-sm text-gray-600 mt-2">Select a new time slot for your appointment. Available slots for the next 7 days are shown below.</p>
						</div>

						{loadingSlots ? (
							<div className="flex justify-center items-center min-h-[200px] sm:min-h-[300px]">
								<Loader show={true} />
							</div>
						) : availableSlots.length === 0 ? (
							<div className="flex flex-col items-center justify-center min-h-[200px] sm:min-h-[300px] text-gray-500">
								<svg className="w-12 h-12 sm:w-16 sm:h-16 mb-3 sm:mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
								</svg>
								<p className="text-base sm:text-lg font-medium">No Available Slots</p>
								<p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-400 text-center px-4">There are no appointment slots available for the selected date range.</p>
							</div>
						) : (
							<>
								<div className="space-y-4 max-h-[400px] sm:max-h-[600px] overflow-y-auto pr-2 sm:pr-4 -mr-2 sm:-mr-4">
									{Object.entries(
										availableSlots.reduce((acc, slot) => {
											const date = moment(slot.date, 'MM/DD/YYYY').format('YYYY-MM-DD');
											if (!acc[date]) acc[date] = [];
											acc[date].push(slot);
											return acc;
										}, {})
									).map(([date, slots]) => (
										<div key={date} className="bg-gray-50 rounded-lg p-3 sm:p-4">
											<h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3">
												{moment(date).format('ddd, MMM D')}
												<span className="hidden sm:inline">, {moment(date).format('YYYY')}</span>
											</h3>
											<div className="grid grid-cols-2 gap-2 sm:gap-3">
												{slots.map((slot, index) => (
													<button
														key={index}
														onClick={() => handleSlotSelection(slot)}
														className={classNames(
															'flex flex-col p-2.5 sm:p-3 rounded-lg transition-all duration-200',
															'border-2',
															selectedSlot === slot
																? 'border-blue-500 bg-blue-50 shadow-sm'
																: 'border-transparent bg-white hover:border-gray-200 hover:shadow-sm'
														)}
													>
														<div className="flex items-center gap-1.5 mb-1.5">
															<svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
															</svg>
															<span className="font-medium text-gray-900 text-sm">
																{slot.starttime}
															</span>
															<span className="text-xs text-gray-500">
																({slot.duration}m)
															</span>
														</div>

														<div className="text-xs font-medium text-gray-700 truncate">
															{slot.patientappointmenttypename || 'Standard Appointment'}
														</div>
													</button>
												))}
											</div>
										</div>
									))}
								</div>

								{selectedSlot && (
									<div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200 flex justify-between items-center">
										<button
											onClick={() => setSelectedSlot(null)}
											className="text-sm text-gray-600 hover:text-gray-800"
										>
											← Choose different slot
										</button>
										<button
											onClick={handleConfirmSlot}
											className="px-4 sm:px-6 py-2.5 bg-blue-600 text-white text-sm sm:text-base rounded-lg font-medium hover:bg-blue-700 transition-colors"
										>
											Continue
										</button>
									</div>
								)}
							</>
						)}
					</div>
				) : (
					<div className="p-4">
						<div className="flex items-center gap-2 mb-6">
							<button
								onClick={handleBackToSlots}
								className="text-gray-600 hover:text-gray-800"
							>
								<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
								</svg>
							</button>
							<h2 className="text-xl sm:text-2xl font-bold text-gray-800">Reschedule Reason</h2>
						</div>

						<div className="mb-4">
							<div className="bg-gray-50 rounded-lg p-3 mb-6">
								<p className="text-sm text-gray-600 mb-2">Selected Time:</p>
								<div className="font-medium text-gray-900">
									{moment(selectedSlot.date, 'MM/DD/YYYY').format('dddd, MMMM D, YYYY')}
								</div>
								<div className="text-gray-600">
									{selectedSlot.starttime} ({selectedSlot.duration} minutes)
								</div>
							</div>
						</div>

						<div className="mb-6">
							<label htmlFor="rescheduleReason" className="block text-sm font-medium text-gray-700 mb-2">
								Reason for Rescheduling
							</label>
							<textarea
								id="rescheduleReason"
								value={rescheduleReason}
								onChange={(e) => setRescheduleReason(e.target.value)}
								placeholder="Enter your reason here..."
								className="w-full min-h-[100px] p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
							/>
							{rescheduleReason.trim() === '' && (
								<p className="mt-2 text-xs text-red-600">Please provide a reason for rescheduling.</p>
							)}
						</div>

						<div className="flex justify-end">
							<button
								onClick={handleConfirmReschedule}
								disabled={!rescheduleReason.trim()}
								className={classNames(
									"px-6 py-2.5 text-sm font-medium rounded-lg transition-colors",
									rescheduleReason.trim()
										? "bg-blue-600 text-white hover:bg-blue-700"
										: "bg-blue-300 text-white cursor-not-allowed"
								)}
							>
								Confirm Reschedule
							</button>
						</div>
					</div>
				)}
			</BaseModal>

			<div className="mb-8">
				<BaseTabs
					tabs={tabs}
					onTab={setCurrentTab}
					current={currentTab}
				/>
			</div>

			<div className="flex flex-col gap-6">
				{tabAppointments.length === 0 ? (
					<div className="text-center py-12 text-gray-500 text-lg">
						<p>No {currentTab} appointments found.</p>
					</div>
				) : (
					tabAppointments.map((appointment) => (
						<div
							key={appointment.jobId}
							className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200"
						>
							<div className="flex justify-between items-center mb-4">
								<h3 className="text-xl font-semibold text-gray-800 m-0">{appointment.title}</h3>
								<span className={classNames('px-3 py-1 rounded-full text-sm font-medium', getStatusColor(appointment.status))}>
									{getStatusText(appointment.status)}
								</span>
							</div>

							<div className="mb-6">
								<div className="flex justify-between items-center py-2 border-b border-gray-100">
									<span className="font-medium text-gray-500 min-w-[100px]">Date:</span>
									<span className="text-gray-800 text-right flex-1">
										{moment(appointment.date).format('MMM DD, YYYY')}
									</span>
								</div>

								{appointment.startTime && (
									<div className="flex justify-between items-center py-2 border-b border-gray-100">
										<span className="font-medium text-gray-500 min-w-[100px]">Start time:</span>
										<span className="text-gray-800 text-right flex-1">
											{moment(appointment.startTime, 'HH:mm').format('hh:mm A')}
										</span>
									</div>
								)}

								{appointment.duration && (
									<div className="flex justify-between items-center py-2 border-b border-gray-100">
										<span className="font-medium text-gray-500 min-w-[100px]">Duration:</span>
										<span className="text-gray-800 text-right flex-1">{appointment.duration} minutes</span>
									</div>
								)}

								{appointment.providerName && (
									<div className="flex justify-between items-center py-2 border-b border-gray-100">
										<span className="font-medium text-gray-500 min-w-[100px]">Provider:</span>
										<span className="text-gray-800 text-right flex-1">{appointment.providerName}</span>
									</div>
								)}

								{appointment.description && (
									<div className="flex justify-between items-center py-2 border-b border-gray-100">
										<span className="font-medium text-gray-500 min-w-[100px]">Description:</span>
										<span className="text-gray-800 text-right flex-1">{appointment.description}</span>
									</div>
								)}

							</div>

							<div className="flex gap-3 justify-end">
								{appointment.status === 'pending' && (
									<>
										{moment(appointment.date).isAfter(moment().add(1, 'day').startOf('day')) && (
											<>
												<button
													onClick={() => {
														setModalAppointment(appointment);
														handleReschedule(appointment);
													}}
													className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-sky-500 shadow-sm hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 transition-colors"
												>
													<img src={IconEdit} alt="Reschedule" className="w-3.5 h-3.5 mr-1" />
													Reschedule
												</button>

												<button
													onClick={() => {
														setModalAppointment(appointment);
														setShowModal('cancel');
													}}
													className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
												>
													<img src={IconDelete} alt="Cancel" className="w-3.5 h-3.5 mr-1" />
													Cancel
												</button>
											</>
										)}
									</>
								)}

								{appointment.status === 'completed' && !appointment.isFinished && (
									<button className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 shadow-sm">
										<img src={IconCompleted} alt="Completed" className="w-3.5 h-3.5 mr-1" />
										Completed
									</button>
								)}
								{appointment.status === 'completed' && (
									<button
										onClick={() => handleBookAgain(appointment)}
										className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
									>
										Book Again
									</button>
								)}
							</div>
						</div>
					))
				)}
				<Loader show={fetching} />
			</div>
		</div>
	);
};

export default observer(AthenaJobs);
